import React from 'react';
import { Check, ExternalLink } from 'lucide-react';
import logoLocaSmartCheck from '../../../assets/logo-locasmartcheck.png';
import { calcularPrecoLocaSmartCheck, PLAN_OPTIONS, PlanType } from '../../../services/tipos';

interface StepAddonsProps {
  vehicleCount: number;
  incluirLocaSmartCheck: boolean;
  setIncluirLocaSmartCheck: (val: boolean) => void;
  selectedPlan: PlanType;
  onNext: () => void;
  onSkip: () => void;
}

const FEATURES = [
  'Vistorias com fotos em alta resolução',
  'Assinatura digital do locatário',
  'Relatórios PDF automáticos',
  'Checklist personalizável por veículo',
  'Histórico completo de todas vistorias',
];

const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/0UtHbl-iexs';
const YOUTUBE_DEMO_URL = 'https://youtu.be/eY0JV3bmnMw';

const StepAddons: React.FC<StepAddonsProps> = ({
  vehicleCount,
  incluirLocaSmartCheck,
  setIncluirLocaSmartCheck,
  selectedPlan,
  onNext,
  onSkip,
}) => {
  const planOption = PLAN_OPTIONS.find(p => p.id === selectedPlan);
  const cycleMonths = planOption?.cycleMonths ?? 1;
  const discount = planOption?.discount ?? 0;
  const precoMensalCheio = calcularPrecoLocaSmartCheck(vehicleCount);
  const precoMensal = precoMensalCheio * (1 - discount);
  const precoCiclo = precoMensal * cycleMonths;

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const getCycleName = () => {
    switch (cycleMonths) {
      case 1: return 'mês';
      case 3: return 'trimestre';
      case 6: return 'semestre';
      case 12: return 'ano';
      default: return 'mês';
    }
  };

  const handleIncluir = () => {
    setIncluirLocaSmartCheck(true);
    onNext();
  };

  const handlePular = () => {
    setIncluirLocaSmartCheck(false);
    onSkip();
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="text-[24px] font-extrabold text-[#1F467E] mb-1">Que tal Potencializar suas vistorias?</h1>
        <p className="text-[14px] text-[#94a3b8]">Conheça o módulo que transforma sua operação</p>
      </div>

      {/* Split layout: info + video */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Left — info */}
        <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div className="flex items-center gap-2 mb-3">
            <img src={logoLocaSmartCheck} alt="LocaSmartCheck" className="w-9 h-9 rounded-lg object-contain" />
            <div>
              <div className="text-base font-bold text-[#132f4c]">LocaSmartCheck</div>
              <div className="text-[11px] text-[#94a3b8]">Módulo de Vistorias Profissionais</div>
            </div>
          </div>

          <ul className="list-none p-0 m-0 mb-3">
            {FEATURES.map((feature) => (
              <li key={feature} className="py-[4px] flex items-center gap-2 text-[13px] text-[#374151]">
                <Check size={14} className="text-[#22c55e] flex-shrink-0" strokeWidth={3} />
                {feature}
              </li>
            ))}
          </ul>

          <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-lg p-[10px] text-center">
            <div className="text-[22px] font-extrabold text-[#132f4c]">
              {formatCurrency(precoCiclo)}
              <span className="text-[12px] font-normal text-[#94a3b8]">/{getCycleName()}</span>
            </div>
            {cycleMonths > 1 && discount > 0 && (
              <div className="text-[11px] text-[#16a34a] font-bold">
                {Math.round(discount * 100)}% de desconto incluso
              </div>
            )}
          </div>
        </div>

        {/* Right — video */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <iframe
            src={YOUTUBE_EMBED_URL}
            title="LocaSmartCheck"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full min-h-[260px] border-none"
            style={{ aspectRatio: '9/13' }}
          />
        </div>
      </div>

      {/* CTAs — tudo na mesma linha */}
      <div className="flex gap-3 items-center">
        <button
          onClick={handleIncluir}
          className="flex-1 py-3 bg-[#1F467E] rounded-[12px] text-center text-white font-bold text-[14px] cursor-pointer border-none hover:bg-[#162f56] transition-colors"
        >
          Incluir LocaSmartCheck
        </button>
        <a
          href={YOUTUBE_DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-3 bg-white border-2 border-[#1F467E] rounded-[12px] text-center text-[#1F467E] font-bold text-[14px] cursor-pointer hover:bg-[#f0f4ff] transition-colors flex items-center justify-center gap-1.5 no-underline"
        >
          Ver demonstração <ExternalLink size={13} />
        </a>
        <button
          onClick={handlePular}
          className="py-3 px-5 text-[13px] text-[#94a3b8] cursor-pointer border border-[#e2e8f0] bg-white rounded-[12px] hover:bg-[#f8fafc] hover:text-[#475569] transition-colors font-medium whitespace-nowrap"
        >
          Pular
        </button>
      </div>
    </div>
  );
};

export default StepAddons;
